import React, { useState, useEffect } from 'react';
import { paymentAPI } from '../services/api';
import { toast } from 'react-toastify';

const ManualPaymentVerification = () => {
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Check if there's a reference stored from quick verify button
  useEffect(() => {
    const storedRef = sessionStorage.getItem('manual_verify_reference');
    if (storedRef) {
      setReference(storedRef);
      sessionStorage.removeItem('manual_verify_reference');
    }
  }, []);

  const handleVerify = async () => {
    if (!reference.trim()) {
      toast.error('Please enter a payment reference');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await paymentAPI.check(reference);
      
      if (response.data.status === 'success') {
        setResult({
          type: 'success',
          message: response.data.message || 'Subscription activated successfully!'
        });
        toast.success('Subscription activated successfully!');
        window.dispatchEvent(new Event('refreshSubscriptionsData'));
        window.dispatchEvent(new Event('notification-read'));
      } else {
        setResult({
          type: 'error',
          message: response.data.message || 'Payment verification failed'
        });
        toast.error('Verification failed');
      }
    } catch (error) {
      console.error('Manual verification error:', error);
      setResult({
        type: 'error',
        message: error.response?.data?.error || 'Failed to verify payment. Please check the reference and try again.'
      });
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Manual Payment Verification</h2>
        <p className="text-sm text-gray-500 mt-1">
          Use this tool to manually verify and activate subscriptions when automatic verification fails
        </p>
      </div>

      <div className="p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Reference
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Enter Paystack reference (e.g., ACS-1734567890123)"
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleVerify}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 transition"
            >
              {loading ? 'Verifying...' : 'Verify & Activate'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Enter the Paystack transaction reference (starts with ACS-)
          </p>
        </div>

        {result && (
          <div className={`p-4 rounded-lg ${result.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start gap-3">
              {result.type === 'success' ? (
                <svg className="w-5 h-5 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <div className="flex-1">
                <p className={`font-medium ${result.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                  {result.type === 'success' ? 'Success!' : 'Verification Failed'}
                </p>
                <p className={`text-sm mt-1 ${result.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                  {result.message}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">How to find the reference:</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Check the URL when customer was redirected: <code className="bg-gray-200 px-1 rounded">/payment/callback?reference=ACS-123456</code></li>
            <li>Check Paystack dashboard under Transactions</li>
            <li>Check the subscription ID in your database (it uses the same reference)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManualPaymentVerification;